import { captureException } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import { CountryCode, LanguageCode } from 'typescript/constants/localization';
import { useLocaleInfo } from 'typescript/context/locale_info';
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { QUERY_KEYS, MUTATION_KEYS } from 'typescript/constants/react_query';
import {
  getShippingRates as clientGetShippingRates,
  selectShippingRate as clientSelectShippingRate,
} from 'typescript/clients/solidshop/shipping_rates_client';
import { ShippingRate } from 'typescript/types/api/shipping_rates';

const useGetShippingRates = ({
  orderNumber,
  countryCode,
  languageCode,
  shouldFetch,
}: {
  orderNumber: string;
  countryCode?: CountryCode;
  languageCode?: LanguageCode;
  shouldFetch?: boolean;
}) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();

  const { data: shippingRates, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.SHIPPING_RATES, { orderNumber }],
    queryFn: () =>
      clientGetShippingRates({
        orderNumber,
        countryCode: countryCode ?? contextCountryCode,
        languageCode: languageCode ?? contextLanguageCode,
      }),
    enabled: shouldFetch, // we only fetch if the address is in the orderData
    staleTime: 1000 * 60 * 5,
  });

  return { shippingRates, ...rest };
};

const useSelectShippingRate = ({ orderNumber }: { orderNumber: string }) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();

  const queryClient = useQueryClient();
  const queryKey = [MUTATION_KEYS.SELECT_SHIPPING_RATE, { orderNumber }];

  const { mutate: selectShippingRate, ...rest } = useMutation({
    mutationKey: queryKey,
    mutationFn: ({
      shippingRateCode,
      countryCode = contextCountryCode,
      languageCode = contextLanguageCode,
    }: {
      shippingRateCode: string;
      countryCode?: CountryCode;
      languageCode?: LanguageCode;
    }) =>
      clientSelectShippingRate({
        orderNumber,
        shippingRateCode,
        countryCode,
        languageCode,
      }),
    onMutate: async ({ shippingRateCode }) => {
      // cancel any queries for the shipping rates to prevent rerender / flickering
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.SHIPPING_RATES, { orderNumber }],
      });

      // get the previous shipping rates to rollback if the selection fails
      const previousShippingRates = queryClient.getQueryData([
        QUERY_KEYS.SHIPPING_RATES,
        { orderNumber },
      ]);

      // optimistically update the selected shipping rate
      queryClient.setQueryData(
        [QUERY_KEYS.SHIPPING_RATES, { orderNumber }],
        (old: ShippingRate[]) => {
          return old.map((shippingRate: ShippingRate) => ({
            ...shippingRate,
            selected: shippingRate.code === shippingRateCode,
          }));
        },
      );

      // return the previous shipping rates to the context of the onError handler
      return { previousShippingRates };
    },
    onError: (error, __, context) => {
      // rollback to the previous shipping rates if the selection fails
      if (context?.previousShippingRates) {
        queryClient.setQueryData(
          [QUERY_KEYS.SHIPPING_RATES, { orderNumber }],
          context.previousShippingRates,
        );
      }

      captureException(
        new CustomError(MUTATION_KEYS.SELECT_SHIPPING_RATE, error),
        {
          extra: {
            orderNumber,
          },
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
      });
    },
  });

  const isAnyPending =
    useIsMutating({
      mutationKey: queryKey,
    }) > 0;

  return { selectShippingRate, isAnyPending, ...rest };
};

export { useGetShippingRates, useSelectShippingRate };
